/**
 * Sort order of the project board (`/by-board`) — pure helpers, no `window`, no `appState`.
 *
 * Kept free of both on purpose: vitest runs with `environment: 'node'` (no `window`), and the
 * action tests mock `app.state` wholesale, so anything they import from here must not reach
 * either. `app.state.ts`, the toggle action, `tenant-feature-flags.ts` and `boards.view.ts` all
 * import from this one module, so the allowed values and the storage key exist exactly once.
 */

/** The two sort orders of the project board. */
export type BoardSort = 'milestone' | 'alphabetical';

/** The sort order that applies when neither the tenant nor the browser says otherwise. */
export const BOARD_SORT_FALLBACK: BoardSort = 'milestone';

/**
 * `@storage` options of `appState.boardSort`. The decorator stores under `${prefix}_${key}`
 * (`@lit-app/state/src/decorators/storage.js`), which `boardSortStorageKey()` mirrors.
 */
export const BOARD_SORT_STORAGE = { key: 'board-sort', prefix: 'ha' } as const;

/** The localStorage key `@storage(BOARD_SORT_STORAGE)` writes to: `ha_board-sort`. */
export function boardSortStorageKey(): string {
    return `${BOARD_SORT_STORAGE.prefix}_${BOARD_SORT_STORAGE.key}`;
}

/**
 * The only validation of a board sort value, for the tenant default and the browser choice alike.
 *
 * Anything else answers `null` ("no valid choice"). That includes the string `"null"`: with an
 * empty store, `@storage` writes the `null` default back as that string on every app start.
 */
export function parseBoardSort(value: unknown): BoardSort | null {
    return value === 'milestone' || value === 'alphabetical' ? value : null;
}

/** The sort order in force: a valid browser choice wins, otherwise the tenant default. */
export function effectiveBoardSort(stored: unknown, tenantDefault: BoardSort): BoardSort {
    return parseBoardSort(stored) ?? tenantDefault;
}

/**
 * What the browser should remember after one press of the toggle.
 *
 * The toggle flips the effective order. Landing on the tenant default answers `null`: the browser
 * then forgets its choice and follows the tenant default again, including later changes to it
 * (Anke's decision 6, 2026-10-07).
 */
export function nextBoardSortChoice(stored: unknown, tenantDefault: BoardSort): BoardSort | null {
    const target: BoardSort = effectiveBoardSort(stored, tenantDefault) === 'milestone' ? 'alphabetical' : 'milestone';
    return target === tenantDefault ? null : target;
}
